import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import styled, { ThemeContext, keyframes } from 'styled-components';
import { withProp } from 'styled-tools';

import Box from '@ui/box/Box.tsx';
import Icon from '@ui/icons/Icon.tsx';
import Text from '@ui/text/Text.tsx';
import Tooltip from '@ui/tooltip';
import { rgba } from '@utils/dom.ts';
import { focusRelativeToElement } from '@utils/focus.ts';
import { getThemeVal } from '@utils/styled-component-tools';

import useComponentFocused from '../../../hooks/useComponentFocused.tsx';

const MenuOptions = styled(Box)`
  padding: unset;
  margin: unset;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  overflow: hidden;
  background-color: ${getThemeVal('colors.background', '#FFFFFF')};
  z-index: 1;
  & > button:first-of-type {
    padding-top: 14px;
  }
  & > button:last-of-type {
    padding-bottom: 14px;
  }
`;

const MenuOption = styled(Box).attrs({ as: 'button' })`
  cursor: pointer;
  border: none;
  background: none;
  color: ${getThemeVal('colors.primaryText', '#333333')};

  &:focus-visible {
    outline: var(--focus-outline);
    outline-offset: -2px;
    outline-width: 2px; // these need to be explicit for Safari
    outline-style: solid;
  }

  &:hover {
    background-color: ${withProp(getThemeVal('colors.primaryText', '#333333'), primaryText => rgba(primaryText, 0.1))};
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(100%);
    pointer-events: none;
  }
  99% {
    pointer-events: none; /* prevent mouse over event until the animation is done */
  }
  to {
    opacity: 1;
    transform: translateX(0);
    pointer-events: auto;
  }
`;

const slideInRtl = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100%);
    pointer-events: none;
  }
  99% {
    pointer-events: none; /* prevent mouse over event until the animation is done */
  }
  to {
    opacity: 1;
    transform: translateX(0);
    pointer-events: auto;
  }
`;

const MiniMenuBox = styled(Box)`
  position: absolute;
  bottom: -6px;
  inset-inline-end: 10px; /* RTL friendly version of right: 10px */
  display: flex;
  align-items: flex-end;
`;

const MiniMenuOptions = styled(Box)`
  padding: unset;
  margin: unset;
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  background-color: ${getThemeVal('colors.background', '#FFFFFF')};
  animation: ${slideIn} 0.2s ease-out forwards;
  z-index: 1;

  [dir='rtl'] & {
    animation: ${slideInRtl} 0.2s ease-out forwards;
  }

  /* Adjust border radius for first and last buttons, so the border looks right when they are focused */
  & > button:first-of-type {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }
  & > button:last-of-type {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const StyledText = styled(Text)`
  font-size: ${getThemeVal('fonts.size.h4', '14px')};
`;

const StyledMenuIcon = styled(Icon)`
  margin-inline-end: 4px;
`;

const StyledTriggerWrapper = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  border-radius: unset;
  background-color: ${getThemeVal('colors.widgetBackground', '#F9F9F9')};
  position: relative;
  inset-inline-start: 4px;
  width: 4px;
  padding: 0px 0px 0px 0px;
  overflow: hidden;

  &:focus-visible {
    outline-offset: 12px;
    outline-width: 2px; // these need to be explicit for Safari
    outline-style: solid; // these need to be explicit for Safari
  }
`;

type MenuItem = {
  iconId: string;
  label: string;
  onClick: () => void;
};

type DraggableOptionsProps = React.ComponentPropsWithoutRef<typeof Box> & {
  T: (key: string) => string;
  isMobile?: boolean;
  menuItems: MenuItem[];
  submenuLabel?: string;
  tooltipContent?: string;
};

const DraggableOptions = (props: DraggableOptionsProps) => {
  const themeContext = useContext(ThemeContext);
  const themeColors = themeContext?.colors ?? {};
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { ref: componentFocusedRef, popupRef, isComponentFocused, setIsComponentFocused } = useComponentFocused(true);
  const {
    menuItems,
    tooltipContent = 'View Actions',
    // This compatibility prop is intentionally consumed so it is not forwarded to the DOM.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    submenuLabel,
    isMobile,
    T,
    'data-testid': dataTestId = 'DraggableOptions',
    ...omittedProps
  } = props;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstOptionRef = useRef<HTMLButtonElement>(null);
  const lastOptionRef = useRef<HTMLButtonElement>(null);
  const menuOptionsRef = useRef<HTMLDivElement>(null);

  /** Compute portal menu position from the trigger button */
  const menuPosition = useMemo(() => {
    if (!isMenuOpen || isMobile || !triggerRef.current) {
      return null;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    const isRtl = document?.documentElement?.getAttribute('dir') === 'rtl';
    if (isRtl) {
      return {
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right + 5,
      };
    }
    return { top: rect.bottom + 4, left: rect.left + 5 };
  }, [isMenuOpen, isMobile]);

  useEffect(() => {
    if (!isComponentFocused && isMenuOpen) {
      setIsMenuOpen(false);
    } // if the outside click was detected, but menu was open, hide it
  }, [isComponentFocused, isMenuOpen]);

  const toggleSubmenu = () => {
    // is on the trigger button
    if (!isMenuOpen) {
      // focus first menu item when opening
      setTimeout(() => firstOptionRef.current?.focus(), 50);
    }
    setIsMenuOpen(!isMenuOpen);
    setIsComponentFocused(!isMenuOpen);
  };

  const onItemClick = (item: MenuItem) => {
    // is on each menuItem
    item.onClick();
    toggleSubmenu();
    // give focus back to trigger after closing submenu
    setTimeout(() => triggerRef.current?.focus(), 50);
  };

  const MenuOptionsWrapperKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // is on the menu options
    if (e.key === 'Tab') {
      // Move focus relative to the button (not the menuitem DOM position), then close.
      const direction = e.shiftKey ? -1 : 1;
      const moved = focusRelativeToElement(triggerRef.current, direction, menuOptionsRef.current);
      if (moved) {
        e.preventDefault();
      }
      setIsMenuOpen(false);
      setIsComponentFocused(false);
      e.stopPropagation();
    } else if (e.key === 'Escape') {
      setIsMenuOpen(false);
      setTimeout(() => triggerRef.current?.focus(), 0);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      (document.activeElement?.previousSibling as HTMLElement | null)?.focus();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      (document.activeElement?.nextSibling as HTMLElement | null)?.focus();
    }
  };

  const firstOrLastRef = (index: number) => {
    if (index === 0) {
      return firstOptionRef;
    }
    if (index === menuItems.length - 1) {
      return lastOptionRef;
    }
    return null;
  };

  const boxKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (document.activeElement === triggerRef?.current) {
      if (isMenuOpen && ((e.key === 'Tab' && e.shiftKey) || e.key === 'Escape')) {
        // check if you cliked the trigger and then tab back or escape
        setIsMenuOpen(false);
        setIsComponentFocused(false);
      }
      // Arrow keys to move focus from parent to menu options
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        lastOptionRef.current?.focus();
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        firstOptionRef.current?.focus();
      }
    }
  };

  return (
    <Box display="flex" ref={componentFocusedRef} onKeyDown={boxKeyDown} data-testid={dataTestId} {...omittedProps}>
      <Tooltip
        dataTestId="SubmenuTooltip"
        text={tooltipContent}
        cancelTooltip={isMenuOpen}
        arrowPosition={'left'}
        placement="left-start"
        gutter={20}
        avoidCover
      >
        <StyledTriggerWrapper
          data-testid="DraggableOptionsTrigger"
          onClick={toggleSubmenu}
          aria-label={tooltipContent}
          ref={triggerRef}
          aria-controls="MenuDialog"
          aria-expanded={isMenuOpen}
        >
          <Icon
            name="submenu"
            wrapper="span"
            iconSize={themeContext?.narrowIconSizes?.lg || '24px'}
            color={themeColors.widgetIconFill}
            degreesOfIconRotation={90}
            backgroundColor="transparent"
            aria-hidden="true"
          />
        </StyledTriggerWrapper>
      </Tooltip>
      {isMenuOpen &&
        (isMobile ? (
          <Box position="relative" display="flex" ref={popupRef}>
            <MiniMenuBox
              id="MenuDialog"
              role="dialog"
              aria-modal="false"
              aria-label={tooltipContent}
              data-testid="DraggableOptionsDialog"
            >
              <MiniMenuOptions
                data-testid="MenuOptions"
                display="flex"
                boxShadow="md"
                as="menu"
                ref={menuOptionsRef}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => MenuOptionsWrapperKeyDown(e)}
              >
                {menuItems.map((item, index) => (
                  <MenuOption
                    key={index}
                    ref={firstOrLastRef(index)}
                    data-testid={`MenuOption-${item.label.replace(/\s/g, '')}`}
                    className="menuOption"
                    onClick={() => onItemClick(item)}
                    display="flex"
                    position="relative"
                    alignItems="center"
                    cursor="pointer"
                    padding="4px 4px 0 4px"
                    tabIndex="0"
                    aria-label={item.label}
                    role="button"
                  >
                    <Tooltip text={T(`getDirectionsFromTo:${item.label}`)} arrowPosition="bottom">
                      <Icon name={item.iconId} iconSize="28px" color={themeColors.primaryText} aria-hidden="true" />
                    </Tooltip>
                  </MenuOption>
                ))}
              </MiniMenuOptions>
            </MiniMenuBox>
          </Box>
        ) : (
          ReactDOM.createPortal(
            <Box
              id="MenuDialog"
              className="atrius-map"
              position="fixed"
              left={menuPosition?.left != null ? `${menuPosition.left}px` : undefined}
              right={menuPosition?.right != null ? `${menuPosition.right}px` : undefined}
              top={`${menuPosition?.top ?? 0}px`}
              display="flex"
              alignItems="flex-end"
              borderRadius="lg"
              boxShadow="md"
              zIndex={1000}
              role="dialog"
              aria-modal="false"
              aria-label={tooltipContent}
              data-testid="DraggableOptionsDialog"
              ref={popupRef}
            >
              <MenuOptions
                data-testid="MenuOptions"
                display="flex"
                flexDirection="column"
                borderRadius="lg"
                overflow="hidden"
                as="menu"
                ref={menuOptionsRef}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => MenuOptionsWrapperKeyDown(e)}
              >
                {menuItems.map((item, index) => (
                  <MenuOption
                    key={index}
                    ref={firstOrLastRef(index)}
                    data-testid={`MenuOption-${item.label.replace(/\s/g, '')}`}
                    className="menuOption"
                    onClick={() => onItemClick(item)}
                    display="flex"
                    position="relative"
                    alignItems="center"
                    cursor="pointer"
                    padding="10px 0 10px 14px"
                    minWidth="141px"
                    tabIndex={'0'}
                    aria-label={item.label}
                    role="button"
                  >
                    <StyledMenuIcon
                      name={item.iconId}
                      iconSize="24"
                      color={themeColors.primaryText}
                      aria-hidden="true"
                    />
                    <StyledText>{T(`getDirectionsFromTo:${item.label}`)}</StyledText>
                  </MenuOption>
                ))}
              </MenuOptions>
            </Box>,
            document.body,
          )
        ))}
    </Box>
  );
};

DraggableOptions.displayName = 'DraggableOptions';

export default DraggableOptions;
